import { getAmplitudeInstance } from '../amplitudeHelpers';
import FilterName from '../enums/FilterName';
import MapBaseLayer from '../enums/MapBaseLayer';
import MapOverlay from '../enums/MapOverlay';
import NavigatorSearchFilter from '../enums/NavigatorSearchFilter';
import OSMCTA from '../enums/OSMCTA';
import PoiActionDetailedLocation from '../enums/PoiActionDetailedLocation';
import PoiInteraction from '../enums/PoiInteraction';
import PoiLocation from '../enums/PoiLocation';
import POISource from '../enums/POISource';

type logPOISelectedParams = {
  collections_applied?: string[];
  collections_available?: string[];
  custom_route_id?: number;
  distance_from_poi_in_miles?: number;
  filters_applied?: FilterName[];
  is_recording?: boolean;
  lat?: number;
  lng?: number;
  map_base_layer?: MapBaseLayer;
  map_overlay?: MapOverlay[];
  method_of_interaction?: PoiInteraction;
  navigator_search_filters_applied?: NavigatorSearchFilter[];
  osm_category?: string;
  osm_cta?: OSMCTA;
  osm_lat?: number;
  osm_lng?: number;
  osm_name?: string;
  poi_detailed_location?: PoiActionDetailedLocation;
  poi_id?: number;
  poi_location?: PoiLocation;
  poi_source?: POISource;
  poi_type_raw?: string;
  route_loaded?: boolean;
  trail_id?: string;
};

/**
 * The user taps/swipes to select or switch the POI. Fires for all POI sources — AllTrails-curated POIs, OSM-sourced points, and community waypoints — distinguished by poi_source.
 * @param params - Attributes to log with the amplitude event
 * @param params.collections_applied - The name for the collections that were set when an action took place.
 * @param params.collections_available - List of strings containing the collections available to the user.
 * @param params.custom_route_id - Fires if the user is selecting a POI while browsing a saved custom route
 * @param params.distance_from_poi_in_miles - The reported distance that the POI is. If in a pre-recording state, this corresponds to the distance from the start of the trail. If the user is in a recording state, this is the distance from the user's current location. For off-trail POIs, this is the distance as the crow flies from the start of the trail (pre-recording) or user position (recording).
 * @param params.filters_applied - List of strings containing the filters applied by the user
 * @param params.is_recording - Boolean indicator if the user was recording at the time the action took place.
 * @param params.lat - Map focus latitude
 * @param params.lng - Map focus longitude
 * @param params.map_base_layer - Name of tile base layer currently selected
 * @param params.map_overlay - The overlay currently applied to the map.
 * @param params.method_of_interaction - The method by which the user reached the POI (e.g. swipe or tap).
 * @param params.navigator_search_filters_applied - Amenity filters active when the user selected a Navigator search result.
 * @param params.osm_category - OSM tag category of the selected POI. Examples include mountain, campsite, waterfall, &tc. Null when poi_source is not 'osm'.
 * @param params.osm_cta - The CTA variant shown in the popover for the selected OSM POI. Null when poi_source is not 'osm'.
 * @param params.osm_lat - Latitude of the OSM POI. Null when poi_source is not 'osm' or unavailable.
 * @param params.osm_lng - Longitude of the OSM POI. Null when poi_source is not 'osm' or unavailable.
 * @param params.osm_name - Display name of the OSM POI. Null when poi_source is not 'osm' or the OSM point has no name (compact popover variant rendered).
 * @param params.poi_detailed_location - Describes the detailed location where the poi selection action took place.
 * @param params.poi_id - Unique identifier of poi tapped
 * @param params.poi_location - Describes the location of the poi where the action took place e.g. explore map or navigator map.
 * @param params.poi_source - Data source of the selected POI (osm, poi, or community waypoint).
 * @param params.poi_type - Describes the specific type of POI. Deprecated for poi_type_raw.
 * @param params.poi_type_raw - Describes the specific type of POI, based on a raw value of the POI type from the server.
 * @param params.route_loaded - Boolean indicator if a map was loaded upon entry to navigator.
 * @param params.trail_id - Trail id of the route displayed if viewing POI in navigator and route is loaded.
 */
const logPOISelected = (
  {
    collections_applied,
    collections_available,
    custom_route_id,
    distance_from_poi_in_miles,
    filters_applied,
    is_recording,
    lat,
    lng,
    map_base_layer,
    map_overlay,
    method_of_interaction,
    navigator_search_filters_applied,
    osm_category,
    osm_cta,
    osm_lat,
    osm_lng,
    osm_name,
    poi_detailed_location,
    poi_id,
    poi_location,
    poi_source,
    poi_type_raw,
    route_loaded,
    trail_id
  }: logPOISelectedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'poi selected',
    {
      'collections applied': collections_applied,
      'collections available': collections_available,
      'custom route id': custom_route_id,
      'distance from poi in miles': distance_from_poi_in_miles,
      'filters applied': filters_applied,
      'is recording': is_recording,
      lat,
      lng,
      'map base layer': map_base_layer,
      'map overlay': map_overlay,
      'method of interaction': method_of_interaction,
      'navigator search filters applied': navigator_search_filters_applied,
      'osm category': osm_category,
      'osm cta': osm_cta,
      'osm lat': osm_lat,
      'osm lng': osm_lng,
      'osm name': osm_name,
      'poi detailed location': poi_detailed_location,
      'poi id': poi_id,
      'poi location': poi_location,
      'poi source': poi_source,
      'poi type raw': poi_type_raw,
      'route loaded': route_loaded,
      'trail id': trail_id
    },
    callback
  );
};

export default logPOISelected;
